// ARCHIVO: src/api/database.js
// UBICACIÓN: C:\xampp\htdocs\react-migration\src\api\database.js
// DESCRIPCIÓN: Funciones para conectar React con las APIs PHP del comercio actual

import { API_ENDPOINTS, getComercioId } from '../config/comercio.js';

// Función para obtener productos del comercio
export const getProductosComercio = async () => {
  try {
    // Obtener token de localStorage (manejado centralmente por App.jsx)
    const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No se encontró token de comercio');
    }
    
    const response = await fetch(`${API_ENDPOINTS.productos}?token=${token}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error en getProductosComercio:', error);
    return { success: false, error: error.message };
  }
};

// Función para eliminar producto del comercio
export const eliminarProducto = async (id) => {
  try {
    const comercioId = await getComercioId();
    const response = await fetch(API_ENDPOINTS.productosEliminar, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        id: id,
        comercio_id: comercioId
      }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error en eliminarProducto:', error);
    return { success: false, error: error.message };
  }
};

// Función para obtener productos de Argentina
export const getProductosAr = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.productosAr);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error en getProductosAr:', error);
    return { success: false, error: error.message };
  }
};

// Función para obtener datos del comercio
export const getComercioData = async () => {
  try {
    // Intentar obtener token de la URL primero, luego del localStorage
    let token = getTokenFromUrl();
    if (!token) {
      token = localStorage.getItem('comercio_token');
    }
    
    if (!token) {
      throw new Error('No se encontró token de comercio');
    }
    
    const response = await fetch(`${API_ENDPOINTS.comercioData}?token=${token}`);
    if (!response.ok) {
      throw new Error('Error al conectar con la base de datos');
    }
    return await response.json();
  } catch (error) {
    console.error('Error obteniendo datos del comercio:', error);
    return {
      success: false,
      error: error.message,
      comercio: null
    };
  }
};

// Función para obtener estadísticas del dashboard del comercio
export const getDashboardStats = async () => {
  try {
    // Intentar obtener token de la URL primero, luego del localStorage
    let token = getTokenFromUrl();
    if (!token) {
      token = localStorage.getItem('comercio_token');
    }
    
    if (!token) {
      throw new Error('No se encontró token de comercio');
    }
    
    const response = await fetch(`${API_ENDPOINTS.dashboardStats}?token=${token}`);
    if (!response.ok) {
      throw new Error('Error al obtener estadísticas');
    }
    return await response.json();
  } catch (error) {
    console.error('Error obteniendo estadísticas:', error);
    return {
      success: false,
      error: error.message,
      stats: {
        totalProductos: 0,
        totalStock: 0,
        ventasHoy: 0,
        productosBajoStock: 0
      }
    };
  }
};

// Función para editar un producto del comercio
export const editarProducto = async (productoData) => {
  try {
    const comercioId = await getComercioId();
    const response = await fetch(API_ENDPOINTS.productosEditar, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...productoData,
        comercio_id: comercioId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al editar producto');
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('🔍 [ERROR] editarProducto - Error capturado:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Función para eliminar imagen del servidor del comercio
export const eliminarImagen = async (imagen_url) => {
  try {
    const comercioId = await getComercioId();

    const response = await fetch(API_ENDPOINTS.eliminarImagen, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        imagen_url,
        comercio_id: comercioId
      })
    });


    if (!response.ok) {
      const errorData = await response.json();
      console.error('🔍 [ERROR] eliminarImagen - Error en respuesta:', errorData);
      throw new Error(errorData.error || 'Error al eliminar imagen');
    }

    const responseData = await response.json();
    return responseData;

  } catch (error) {
    console.error('🔍 [ERROR] eliminarImagen - Error capturado:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Función para subir imagen al servidor del comercio
export const subirImagen = async (archivo) => {
  try {
    const comercioId = await getComercioId();

    const formData = new FormData();
    formData.append('imagen', archivo);
    formData.append('comercio_id', comercioId);

    const response = await fetch(API_ENDPOINTS.uploadImagen, {
      method: 'POST',
      body: formData
    });


    if (!response.ok) {
      const errorData = await response.json();
      console.error('🔍 [ERROR] subirImagen - Error en respuesta:', errorData);
      throw new Error(errorData.error || 'Error al subir imagen');
    }

    const responseData = await response.json();
    return responseData;

  } catch (error) {
    console.error('🔍 [ERROR] subirImagen - Error capturado:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Función para obtener inventario del comercio actual
export const getInventarioComercio = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.inventario);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error en getInventarioComercio:', error);
    return { success: false, error: error.message };
  }
};

// Función para obtener productos con stock bajo del comercio actual
export const getStockBajoComercio = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.stockBajo);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error en getStockBajoComercio:', error);
    return { success: false, error: error.message };
  }
};

// Función para obtener productos agotados del comercio actual
export const getProductosAgotadosComercio = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.productosAgotados);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error en getProductosAgotadosComercio:', error);
    return { success: false, error: error.message };
  }
};

// Función para obtener ventas del comercio actual
export const getVentasComercio = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.ventas);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error en getVentasComercio:', error);
    return { success: false, error: error.message };
  }
};

// Función para crear una nueva venta en el comercio
export const crearVenta = async (ventaData) => {
  try {
    const comercioId = await getComercioId();
    const response = await fetch(API_ENDPOINTS.ventasCrear, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...ventaData,
        comercio_id: comercioId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al crear venta');
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('Error en crearVenta:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
